import React, { useEffect, useState, useMemo } from 'react';
import api from '../../services/apiClient.ts';
import { RefreshCw, FileText, Search } from 'lucide-react';

const API_BASE = '/v1';
const API_URL = `${API_BASE}/reports/group-revenues-expenses`;

const headersAuth = {
  'X-Tenant': localStorage.getItem('tenant') || 'default-tenant-1',
  Accept: 'application/json',
};

function formatMoney(val) {
  const num = Number(val ?? 0);
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

const GroupRevenuesExpensesReport = () => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_revenue: 0, total_expenses: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterName, setFilterName] = useState('');

  const filteredRows = useMemo(() => {
    const q = (filterName || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.group_name || '').toLowerCase().includes(q));
  }, [rows, filterName]);

  const filteredTotals = useMemo(() => {
    return {
      total_revenue: filteredRows.reduce((s, r) => s + Number(r.total_revenue ?? 0), 0),
      total_expenses: filteredRows.reduce((s, r) => s + Number(r.total_expenses ?? 0), 0),
    };
  }, [filteredRows]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await api.get(API_URL, { headers: headersAuth });
      const data = resp?.data?.data ?? resp?.data ?? [];
      setRows(Array.isArray(data) ? data : []);
      setTotals(resp?.data?.totals ?? { total_revenue: 0, total_expenses: 0 });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Network Error');
      setRows([]);
      setTotals({ total_revenue: 0, total_expenses: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="text-gray-900 dark:text-gray-100 py-6 px-4 sm:px-6 lg:px-8">
      <div className="w-full mx-auto">
        {/* Header Card - لون مختلف (أخضر/زمردي) */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 mb-8">
          <div className="overflow-hidden rounded-t-2xl bg-gradient-to-r from-emerald-500 to-teal-600 px-8 py-6">
            <div className="flex items-center space-x-reverse space-x-3">
              <div className="bg-white/20 rounded-full p-3">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">إجمالي إيرادات وصرفيات المجموعات</h2>
                <p className="text-emerald-100 mt-1">الإيرادات من مبلغ البيع للمسافرين — الصرفيات من صرفيات المسافرين + صرفيات المجموعة العامة</p>
              </div>
            </div>
          </div>

          <div className="p-8 overflow-visible">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
              <div className="w-full sm:max-w-md">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">فلترة حسب اسم المجموعة</label>
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                    placeholder="بحث باسم المجموعة..."
                    className="w-full pr-10 pl-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-right"
                  />
                </div>
              </div>
              <div className="flex sm:justify-end">
                <button
                  onClick={fetchData}
                  disabled={loading}
                  className="inline-flex items-center justify-center px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-medium shadow-sm transition-colors duration-200"
                  title="تحديث"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2 ${loading ? 'animate-spin' : ''}`} />
                  {loading ? 'جاري الجلب...' : 'تحديث'}
                </button>
              </div>
            </div>
            {error && (
              <div className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</div>
            )}
          </div>
        </div>

        {/* الجدول - مع scroll عمودي */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700">
          <div className="app-table-wrap">
            <table className="app-table min-w-full">
              <thead className="sticky top-0 z-10 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                <tr>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">اسم المجموعة</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">اجمالي الإيرادات (د.ع)</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">اجمالي الصرفيات (د.ع)</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      جاري الجلب...
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      {filterName.trim() ? 'لا توجد مجموعات تطابق البحث' : 'لا توجد بيانات'}
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((r) => (
                    <tr key={r.group_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{r.group_name || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                        {formatMoney(r.total_revenue)} د.ع
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-red-700 dark:text-red-400">
                        {formatMoney(r.total_expenses)} د.ع
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="sticky bottom-0 z-10 bg-gray-50 dark:bg-gray-900 border-t-2 border-gray-200 dark:border-gray-700 shadow-[0_-2px_6px_rgba(0,0,0,0.05)] dark:shadow-[0_-2px_6px_rgba(0,0,0,0.2)]">
                <tr>
                  <td className="px-6 py-4 text-right text-sm font-bold text-gray-700 dark:text-gray-200">المجموع</td>
                  <td className="px-6 py-4 text-right text-sm font-extrabold text-emerald-700 dark:text-emerald-400">
                    {formatMoney(filteredTotals.total_revenue)} د.ع
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-extrabold text-red-700 dark:text-red-400">
                    {formatMoney(filteredTotals.total_expenses)} د.ع
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupRevenuesExpensesReport;
